import React from 'react';
import {
    Box,
    Button,
    Badge,
    CircularProgress,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { DeviceState, PairedDevice, UploadProgressPayload } from 'api/cancam/types';

interface DeviceControlsProps {
    connectedDevice: PairedDevice | null;
    deviceState: DeviceState | null;
    uploadProgress: UploadProgressPayload | null;
    onStartRecording: () => void;
    onStopRecording: () => void;
    onForceUpload: () => void;
    disabled?: boolean;
}

const DeviceControls: React.FC<DeviceControlsProps> = ({
    connectedDevice,
    deviceState,
    uploadProgress,
    onStartRecording,
    onStopRecording,
    onForceUpload,
    disabled = false,
}) => {
    const hasDevice = connectedDevice !== null;
    const isRecording = deviceState?.isRecording ?? false;
    const pendingUploads = deviceState?.pendingUploads ?? 0;
    const isUploading = uploadProgress !== null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mt: 2,
            }}
        >
            {/* Recording Controls */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<FiberManualRecordIcon />}
                    onClick={onStartRecording}
                    disabled={disabled || !hasDevice || isRecording}
                    sx={{ flex: 1 }}
                >
                    Start Recording
                </Button>

                <Button
                    variant="contained"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={onStopRecording}
                    disabled={disabled || !hasDevice || !isRecording}
                    sx={{ flex: 1 }}
                >
                    Stop Recording
                </Button>
            </Box>

            {/* Upload Control */}
            <Badge
                badgeContent={pendingUploads}
                color="primary"
                invisible={pendingUploads === 0}
                sx={{ width: '100%' }}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={
                        isUploading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <CloudUploadIcon />
                        )
                    }
                    onClick={onForceUpload}
                    disabled={disabled || !hasDevice || pendingUploads === 0 || isUploading}
                    fullWidth
                >
                    {isUploading
                        ? `Uploading... ${uploadProgress?.percent ?? 0}%`
                        : 'Force Upload'}
                </Button>
            </Badge>
        </Box>
    );
};

export default DeviceControls;
